
import { useState, useEffect, useCallback, useRef, createContext, useContext, type ReactNode } from 'react';
import { client as supabase } from '@/integrations/neon/client';
import { useAuth } from './useAuth';
import { useSubscription, FREE_REVIEW_DATE_LIMIT } from './useSubscription';
import { toast } from 'sonner';
import { classifyDecisionReviews, classifyReview } from '@/lib/review-classification';
import { formatDateISO, toDate } from '../../utils/date';

const MIN_REFRESH_INTERVAL_MS = 15_000;
const RECONCILIATION_INTERVAL_MS = 15 * 60 * 1000;
const RATING_PAGE_SIZE = 10;
const REVIEW_PAGE_SIZE = 10;

type DecisionSummaryRow = {
  id: string;
  title: string;
  description: string | null;
  rationale: string;
  context: string | null;
  decision_date: string;
  day0_rating: number;
  review_intervals: number[];
  created_at: string;
  updated_at: string;
  current_rating: number;
  current_rating_day: number;
  current_rating_date: string | null;
  due_now_review_count: number;
  overdue_review_count: number;
  upcoming_review_count: number;
  completed_review_count: number;
  future_review_count: number;
  tag_names: string[];
  emotion_names: string[];
};

/**
 * Rows as the Data API returns them. Columns that are nullable in the schema
 * arrive as null, where the mapped Decision shape uses optional keys instead.
 */
type RatingEntryRow = {
  id: string;
  day: number;
  rating: number;
  date: string;
  notes: string | null;
  outcome: string | null;
  lessons: string | null;
};

type ScheduledReviewRow = {
  id: string;
  day: number;
  due_date: string;
  status: string;
  completed_at: string | null;
};

type NamedRow = { id: string; name: string };

/**
 * `exactOptionalPropertyTypes` is on, so an empty column has to produce an
 * absent key rather than an explicit undefined.
 */
function toRatingEntry(row: RatingEntryRow): RatingEntry {
  return {
    id: row.id,
    day: row.day,
    rating: row.rating,
    date: row.date,
    ...(row.notes ? { notes: row.notes } : {}),
    ...(row.outcome ? { outcome: row.outcome } : {}),
    ...(row.lessons ? { lessons: row.lessons } : {}),
  };
}

function toScheduledReview(row: ScheduledReviewRow): Decision['scheduled_reviews'][number] {
  return {
    id: row.id,
    day: row.day,
    due_date: row.due_date,
    status: row.status,
    ...(row.completed_at ? { completed_at: row.completed_at } : {}),
  };
}

const MAX_SUMMARY_TERMS = 12;

export interface RatingEntry {
  id: string;
  day: number;
  rating: number;
  date: string;
  notes?: string;
  outcome?: string;
  lessons?: string;
}

export interface Decision {
  id: string;
  title: string;
  description?: string;
  rationale: string;
  context?: string;
  decision_date: string;
  day0_rating: number;
  review_intervals: number[];
  created_at: string;
  updated_at?: string;
  rating_history: RatingEntry[];
  tags: Array<{ id: string; name: string }>;
  emotions: Array<{ id: string; name: string }>;
  scheduled_reviews: Array<{
    id: string;
    day: number;
    due_date: string;
    status: string;
    completed_at?: string;
  }>;
  has_more_ratings?: boolean;
  has_more_reviews?: boolean;
}

export interface CreateDecisionData {
  title: string;
  description?: string;
  rationale: string;
  context?: string;
  decision_date: string;
  day0_rating: number;
  review_intervals: number[];
  tag_names?: string[];
  emotion_names?: string[];
}

function useDecisionsState() {
  const [decisions, setDecisions] = useState<Decision[]>([]);
  const [detailsByDecisionId, setDetailsByDecisionId] = useState<Record<string, Decision>>({});
  const [staleDetails, setStaleDetails] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { getReviewDateLimit } = useSubscription();

  const [isRefreshing, setIsRefreshing] = useState(false);

  // Debounce and throttle refreshes to prevent excessive calls
  const refreshTimeoutRef = useRef<NodeJS.Timeout>();
  const lastRefreshRef = useRef(0);
  const decisionsRef = useRef<Decision[]>([]);
  const detailsByDecisionIdRef = useRef<Record<string, Decision>>({});
  const staleDetailsRef = useRef<Record<string, boolean>>({});
  const isRefreshingRef = useRef(false);
  const detailFetchesRef = useRef<Record<string, Promise<Decision | null>>>({});
  const detailFetchedAtRef = useRef<Record<string, number>>({});
  const refreshAgainWhenDoneRef = useRef(false);
  // fetchDecisions re-runs itself when a refresh arrives mid-flight; going
  // through a ref keeps that from making the callback depend on itself.
  const fetchDecisionsRef = useRef<(force?: boolean) => Promise<void>>();
  const isVisibleRef = useRef(typeof document === 'undefined' ? true : document.visibilityState === 'visible');
  const refreshQueuedWhileHiddenRef = useRef(false);
  const payloadValidationDoneRef = useRef(false);

  useEffect(() => {
    decisionsRef.current = decisions;
  }, [decisions]);

  useEffect(() => {
    detailsByDecisionIdRef.current = detailsByDecisionId;
  }, [detailsByDecisionId]);

  useEffect(() => {
    staleDetailsRef.current = staleDetails;
  }, [staleDetails]);

  useEffect(() => {
    isRefreshingRef.current = isRefreshing;
  }, [isRefreshing]);

  // The summary view returns per-status counts, not review rows, so the list
  // views need rows rebuilt client-side. decision_date + review_intervals
  // reproduce exactly what the create_scheduled_reviews trigger scheduled, so
  // day and due_date are the real values rather than an offset from today.
  // Completion is the one thing the summary cannot express per row; the
  // completed count is applied to the earliest reviews, since a review can only
  // be completed once it is due. The detail view does not use these rows — it
  // loads the real ones through openDecisionDetails.
  const buildSummaryReviews = (summary: DecisionSummaryRow): Decision['scheduled_reviews'] => {
    const intervals = [...(summary.review_intervals || [])].sort((a, b) => a - b);
    if (intervals.length === 0) return [];

    let remainingCompleted = summary.completed_review_count || 0;

    return intervals.map((interval) => {
      const dueDate = toDate(summary.decision_date);
      dueDate.setDate(dueDate.getDate() + interval);

      const isCompleted = remainingCompleted > 0;
      if (isCompleted) remainingCompleted -= 1;

      const id = `${summary.id}-day-${interval}`;
      const isoDueDate = formatDateISO(dueDate);

      if (isCompleted) {
        return {
          id,
          day: interval,
          due_date: isoDueDate,
          status: 'completed',
          completed_at: summary.current_rating_date || summary.updated_at,
        };
      }

      return {
        id,
        day: interval,
        due_date: isoDueDate,
        status: classifyReview({ id, day: interval, due_date: isoDueDate, status: 'upcoming' }),
      };
    });
  };

  const fetchDecisionSummaries = useCallback(async () => {
    if (!user) return [] as Decision[];

    const { data, error } = await supabase
      .from('decision_summaries')
      .select(`
        id,
        title,
        description,
        rationale,
        context,
        decision_date,
        day0_rating,
        review_intervals,
        created_at,
        updated_at,
        current_rating,
        current_rating_day,
        current_rating_date,
        due_now_review_count,
        overdue_review_count,
        upcoming_review_count,
        completed_review_count,
        future_review_count,
        tag_names,
        emotion_names
      `)
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return ((data || []) as DecisionSummaryRow[]).map((summary): Decision => {
      const tagNames = (summary.tag_names || []).slice(0, MAX_SUMMARY_TERMS);
      const emotionNames = (summary.emotion_names || []).slice(0, MAX_SUMMARY_TERMS);

      const scheduledReviews = buildSummaryReviews(summary);

      return {
        id: summary.id,
        title: summary.title,
        ...(summary.description ? { description: summary.description } : {}),
        rationale: summary.rationale,
        ...(summary.context ? { context: summary.context } : {}),
        decision_date: summary.decision_date,
        day0_rating: summary.day0_rating,
        review_intervals: summary.review_intervals,
        created_at: summary.created_at,
        ...(summary.updated_at ? { updated_at: summary.updated_at } : {}),
        rating_history: [
          {
            id: `${summary.id}-current-rating`,
            day: summary.current_rating_day || 0,
            rating: summary.current_rating,
            date: summary.current_rating_date || summary.created_at,
          },
        ],
        tags: tagNames.map((name) => ({ id: `${summary.id}-tag-${name}`, name })),
        emotions: emotionNames.map((name) => ({ id: `${summary.id}-emotion-${name}`, name })),
        scheduled_reviews: scheduledReviews,
      };
    });
  }, [user]);
  
  const fetchDecisions = useCallback(async (force = false) => {
    if (!user) {
      if (decisionsRef.current.length === 0) {
        setLoading(false);
      }
      return;
    }

    if (!force && !isVisibleRef.current) {
      refreshQueuedWhileHiddenRef.current = true;
      return;
    }

    if (isRefreshingRef.current) {
      // Dropping this request would lose the refresh a just-completed write is
      // waiting on, so run it again once the in-flight fetch finishes.
      refreshAgainWhenDoneRef.current = true;
      return;
    }

    try {
      setIsRefreshing(true);
      isRefreshingRef.current = true;
      lastRefreshRef.current = Date.now();
      const startedAt = lastRefreshRef.current;

      if (decisionsRef.current.length === 0) {
        setLoading(true);
      }

      const transformedDecisions = await fetchDecisionSummaries();

      const summaryPayloadBytes = transformedDecisions.reduce(
        (sum, decision) => sum + JSON.stringify(decision).length,
        0
      );
      console.log(`Decision summary payload: ${summaryPayloadBytes} bytes across ${transformedDecisions.length} decisions`);

      if (!payloadValidationDoneRef.current && transformedDecisions.length > 0) {
        const sampleDecision = transformedDecisions[0];
        const { data: legacyPayload, error: legacyError } = await supabase
          .from('decisions')
          .select(`
            id,
            title,
            description,
            rationale,
            context,
            decision_date,
            day0_rating,
            review_intervals,
            created_at,
            updated_at,
            rating_entries!decision_id (
              id,
              day,
              rating,
              date,
              notes,
              outcome,
              lessons
            ),
            decision_tags!decision_id (
              tags (
                id,
                name
              )
            ),
            decision_emotions!decision_id (
              emotions (
                id,
                name
              )
            ),
            scheduled_reviews!decision_id (
              id,
              day,
              due_date,
              status,
              completed_at
            )
          `)
          .eq('id', sampleDecision.id)
          .eq('user_id', user.id)
          .maybeSingle();

        if (!legacyError && legacyPayload) {
          const summaryBytes = JSON.stringify(sampleDecision).length;
          const fullBytes = JSON.stringify(legacyPayload).length;
          console.log(
            `Decision payload validation (id=${sampleDecision.id}): summary=${summaryBytes} bytes, full=${fullBytes} bytes, smaller=${summaryBytes < fullBytes}`
          );
        }

        const maxTagCount = Math.max(...transformedDecisions.map((decision) => decision.tags.length));
        const maxEmotionCount = Math.max(...transformedDecisions.map((decision) => decision.emotions.length));
        console.log(`Decision summary bounds: max_tags=${maxTagCount}, max_emotions=${maxEmotionCount}, max_allowed=${MAX_SUMMARY_TERMS}`);

        payloadValidationDoneRef.current = true;
      }

      setDecisions(transformedDecisions);
      // Keep the cached per-decision details on screen but mark them stale, so
      // an open detail view refetches its real rows without blanking first.
      // Details fetched since this refresh started are already current — a
      // write refetches them directly — so leave those alone.
      setStaleDetails(
        Object.fromEntries(
          Object.keys(detailsByDecisionIdRef.current)
            .filter((id) => (detailFetchedAtRef.current[id] || 0) < startedAt)
            .map((id) => [id, true]),
        ),
      );
      console.log(`Fetched ${transformedDecisions.length} decisions successfully`);
    } catch (error) {
      console.error('Error fetching decisions:', error);
      if (decisionsRef.current.length === 0) {
        toast.error('Failed to load decisions');
      }
    } finally {
      setLoading(false);
      setIsRefreshing(false);
      isRefreshingRef.current = false;

      if (refreshAgainWhenDoneRef.current) {
        refreshAgainWhenDoneRef.current = false;
        void fetchDecisionsRef.current?.(true);
      }
    }
  }, [user, fetchDecisionSummaries]);

  useEffect(() => {
    fetchDecisionsRef.current = fetchDecisions;
  }, [fetchDecisions]);

  const loadMoreRatings = useCallback(async (decisionId: string, cursor: number) => {
    const { data, error } = await supabase
      .from('rating_entries')
      .select('id, day, rating, date, notes, outcome, lessons')
      .eq('decision_id', decisionId)
      .order('date', { ascending: false })
      .order('day', { ascending: false })
      .range(cursor, cursor + RATING_PAGE_SIZE);

    if (error) {
      console.error('Error loading more ratings:', error);
      toast.error('Failed to load older rating entries');
      throw error;
    }

    const mappedRatings = (data || []).map(toRatingEntry);

    const nextRatings = mappedRatings.slice(0, RATING_PAGE_SIZE);
    const hasMoreRatings = mappedRatings.length > RATING_PAGE_SIZE;

    setDecisions((prev) =>
      prev.map((decision) =>
        decision.id === decisionId
          ? {
              ...decision,
              rating_history: [...decision.rating_history, ...nextRatings],
              has_more_ratings: hasMoreRatings,
            }
          : decision
      )
    );
  }, []);

  const loadMoreReviews = useCallback(async (decisionId: string, cursor: number) => {
    const { data, error } = await supabase
      .from('scheduled_reviews')
      .select('id, day, due_date, status, completed_at')
      .eq('decision_id', decisionId)
      .order('day', { ascending: false })
      .order('due_date', { ascending: false })
      .range(cursor, cursor + REVIEW_PAGE_SIZE);

    if (error) {
      console.error('Error loading more reviews:', error);
      toast.error('Failed to load older reviews');
      throw error;
    }

    const mappedReviews = (data || []).map(toScheduledReview);

    const nextReviews = mappedReviews.slice(0, REVIEW_PAGE_SIZE);
    const hasMoreReviews = mappedReviews.length > REVIEW_PAGE_SIZE;

    setDecisions((prev) =>
      prev.map((decision) =>
        decision.id === decisionId
          ? {
              ...decision,
              scheduled_reviews: [...decision.scheduled_reviews, ...nextReviews],
              has_more_reviews: hasMoreReviews,
            }
          : decision
      )
    );
  }, []);

  const debouncedRefresh = useCallback((force = false) => {
    // Clear existing timeout
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
    
    // Only refresh if enough time has passed since last refresh
    const now = Date.now();
    const timeSinceLastRefresh = now - lastRefreshRef.current;
    const minRefreshInterval = force ? 0 : MIN_REFRESH_INTERVAL_MS;
    
    if (timeSinceLastRefresh < minRefreshInterval) {
      // Schedule refresh for later
      refreshTimeoutRef.current = setTimeout(() => {
        fetchDecisions(force);
      }, minRefreshInterval - timeSinceLastRefresh);
    } else {
      // Refresh immediately
      fetchDecisions(force);
    }
  }, [fetchDecisions]);

  const fetchDecisionById = useCallback(async (decisionId: string) => {
    if (!user) return null;

    const { data, error } = await supabase
      .from('decisions')
      .select(`
        id,
        title,
        description,
        rationale,
        context,
        decision_date,
        day0_rating,
        review_intervals,
        created_at,
        updated_at,
        rating_entries!decision_id (
          id,
          day,
          rating,
          date,
          notes,
          outcome,
          lessons
        ),
        decision_tags!decision_id (
          tags (
            id,
            name
          )
        ),
        decision_emotions!decision_id (
          emotions (
            id,
            name
          )
        ),
        scheduled_reviews!decision_id (
          id,
          day,
          due_date,
          status,
          completed_at
        )
      `)
      .eq('id', decisionId)
      .eq('user_id', user.id)
      .maybeSingle();

    if (error) throw error;
    if (!data) return null;

    return {
      id: data.id,
      title: data.title,
      description: data.description ? data.description : undefined,
      rationale: data.rationale,
      context: data.context ? data.context : undefined,
      decision_date: data.decision_date,
      day0_rating: data.day0_rating,
      review_intervals: data.review_intervals,
      created_at: data.created_at,
      updated_at: data.updated_at || undefined,
      rating_history: (data.rating_entries || []).map(toRatingEntry),
      tags: (data.decision_tags || [])
        .map((dt: { tags: NamedRow | null }) => dt.tags)
        .filter((tag: NamedRow | null): tag is NamedRow => Boolean(tag?.name)),
      emotions: (data.decision_emotions || [])
        .map((de: { emotions: NamedRow | null }) => de.emotions)
        .filter((emotion: NamedRow | null): emotion is NamedRow => Boolean(emotion?.name)),
      scheduled_reviews: [...(data.scheduled_reviews || [])].sort(
        (a: { day: number }, b: { day: number }) => a.day - b.day,
      ),
    } as Decision;
  }, [user]);

  const openDecisionDetails = useCallback(async (decisionId: string, force = false) => {
    if (!force) {
      const cached = detailsByDecisionIdRef.current[decisionId];
      if (cached && !staleDetailsRef.current[decisionId]) {
        return cached;
      }
    }

    // Callers include a render effect, which can fire twice for the same id
    // before the first fetch resolves. Share the in-flight request instead of
    // issuing a second one.
    const inFlight = detailFetchesRef.current[decisionId];
    if (inFlight) return inFlight;

    const request = (async () => {
      try {
        const decision = await fetchDecisionById(decisionId);
        if (!decision) return null;

        setDetailsByDecisionId((current) => ({
          ...current,
          [decisionId]: decision,
        }));
        setStaleDetails((current) => ({
          ...current,
          [decisionId]: false,
        }));
        detailFetchedAtRef.current[decisionId] = Date.now();

        return decision;
      } finally {
        delete detailFetchesRef.current[decisionId];
      }
    })();

    detailFetchesRef.current[decisionId] = request;
    return request;
  }, [fetchDecisionById]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      isVisibleRef.current = document.visibilityState === 'visible';

      if (isVisibleRef.current && refreshQueuedWhileHiddenRef.current) {
        refreshQueuedWhileHiddenRef.current = false;
        debouncedRefresh(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [debouncedRefresh]);

  // Clear any pending debounced refresh on unmount. (The Supabase realtime
  // subscriptions were removed in the Neon migration; the focus/interval
  // reconciliation effects below keep data fresh instead.)
  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);

  // Helper function to get or create a tag
  const getOrCreateTag = useCallback(async (tagName: string) => {
    if (!user) return null;
    
    try {
      // First, try to find existing tag for current user
      const { data: existingTag, error: selectError } = await supabase
        .from('tags')
        .select('*')
        .eq('name', tagName)
        .eq('user_id', user.id)
        .maybeSingle();

      if (selectError && selectError.code !== 'PGRST116') {
        console.error('Error selecting tag:', selectError);
        return null;
      }

      if (existingTag) {
        return existingTag;
      }

      // If tag doesn't exist, create it for current user
      const { data: newTag, error: insertError } = await supabase
        .from('tags')
        .insert({ name: tagName, user_id: user.id })
        .select()
        .single();

      if (insertError) {
        console.error('Error creating tag:', insertError);
        return null;
      }

      return newTag;
    } catch (error) {
      console.error('Error in getOrCreateTag:', error);
      return null;
    }
  }, [user]);

  // Helper function to get or create an emotion
  const getOrCreateEmotion = useCallback(async (emotionName: string) => {
    if (!user) return null;
    
    try {
      // First, try to find existing emotion for current user
      const { data: existingEmotion, error: selectError } = await supabase
        .from('emotions')
        .select('*')
        .eq('name', emotionName)
        .eq('user_id', user.id)
        .maybeSingle();

      if (selectError && selectError.code !== 'PGRST116') {
        console.error('Error selecting emotion:', selectError);
        return null;
      }

      if (existingEmotion) {
        return existingEmotion;
      }

      // If emotion doesn't exist, create it for current user
      const { data: newEmotion, error: insertError } = await supabase
        .from('emotions')
        .insert({ name: emotionName, user_id: user.id })
        .select()
        .single();

      if (insertError) {
        console.error('Error creating emotion:', insertError);
        return null;
      }

      return newEmotion;
    } catch (error) {
      console.error('Error in getOrCreateEmotion:', error);
      return null;
    }
  }, [user]);

  // Create a new decision
  const createDecision = useCallback(async (decisionData: CreateDecisionData) => {
    if (!user) throw new Error('User not authenticated');

    // Decisions are unlimited on every plan. The only gate here is the number
    // of review dates, which the free plan caps.
    const requestedReviewDates = decisionData.review_intervals.filter((i) => i > 0).length;
    if (requestedReviewDates > getReviewDateLimit()) {
      throw new Error(
        `The free plan allows ${FREE_REVIEW_DATE_LIMIT} review dates per decision. Upgrade to add more.`,
      );
    }

    try {
      // First, create the decision
      const { data: decision, error: decisionError } = await supabase
        .from('decisions')
        .insert({
          user_id: user.id,
          title: decisionData.title,
          description: decisionData.description || null,
          rationale: decisionData.rationale,
          context: decisionData.context || null,
          decision_date: decisionData.decision_date,
          day0_rating: decisionData.day0_rating,
          review_intervals: decisionData.review_intervals,
        })
        .select()
        .single();

      if (decisionError) throw decisionError;

      // Handle tags
      if (decisionData.tag_names && decisionData.tag_names.length > 0) {
        for (const tagName of decisionData.tag_names) {
          const tag = await getOrCreateTag(tagName);
          
          if (tag) {
            const { error: linkError } = await supabase
              .from('decision_tags')
              .insert({
                decision_id: decision.id,
                tag_id: tag.id,
                user_id: user.id,
              });
            
            if (linkError) {
              console.error('Error linking tag to decision:', linkError);
            }
          }
        }
      }

      // Handle emotions
      if (decisionData.emotion_names && decisionData.emotion_names.length > 0) {
        for (const emotionName of decisionData.emotion_names) {
          const emotion = await getOrCreateEmotion(emotionName);
          
          if (emotion) {
            const { error: linkError } = await supabase
              .from('decision_emotions')
              .insert({
                decision_id: decision.id,
                emotion_id: emotion.id,
                user_id: user.id,
              });
            
            if (linkError) {
              console.error('Error linking emotion to decision:', linkError);
            }
          }
        }
      }

      toast.success('Decision created successfully!');

      debouncedRefresh(true);

      return decision.id;
    } catch (error) {
      console.error('Error creating decision:', error);
      toast.error('Failed to create decision');
      throw error;
    }
  }, [user, getReviewDateLimit, debouncedRefresh, getOrCreateTag, getOrCreateEmotion]);

  // Update a decision
  const updateDecision = useCallback(async (id: string, updates: Partial<CreateDecisionData>) => {
    if (!user) throw new Error('User not authenticated');

    // Same cap as createDecision, so no edit path can exceed the free plan's
    // review dates.
    if (updates.review_intervals !== undefined) {
      const requestedReviewDates = updates.review_intervals.filter((i) => i > 0).length;
      if (requestedReviewDates > getReviewDateLimit()) {
        throw new Error(
          `The free plan allows ${FREE_REVIEW_DATE_LIMIT} review dates per decision. Upgrade to add more.`,
        );
      }
    }

    try {
      // Only update fields that are provided
      // Only the columns the caller actually supplied, so an omitted field is
      // left alone rather than overwritten with undefined.
      const updateData: Partial<{
        title: string;
        description: string | null;
        rationale: string;
        context: string | null;
        decision_date: string;
        day0_rating: number;
        review_intervals: number[];
      }> = {};
      if (updates.title !== undefined) updateData.title = updates.title;
      if (updates.description !== undefined) updateData.description = updates.description || null;
      if (updates.rationale !== undefined) updateData.rationale = updates.rationale;
      if (updates.context !== undefined) updateData.context = updates.context || null;
      if (updates.decision_date !== undefined) updateData.decision_date = updates.decision_date;
      if (updates.day0_rating !== undefined) updateData.day0_rating = updates.day0_rating;
      if (updates.review_intervals !== undefined) updateData.review_intervals = updates.review_intervals;

      const { error } = await supabase
        .from('decisions')
        .update(updateData)
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;

      // Handle tag updates
      if (updates.tag_names !== undefined) {
        // Remove existing tags
        await supabase
          .from('decision_tags')
          .delete()
          .eq('decision_id', id);

        // Add new tags
        for (const tagName of updates.tag_names) {
          const tag = await getOrCreateTag(tagName);
          
          if (tag) {
            const { error: linkError } = await supabase
              .from('decision_tags')
              .insert({
                decision_id: id,
                tag_id: tag.id,
                user_id: user.id,
              });
            
            if (linkError) {
              console.error('Error linking tag to decision:', linkError);
            }
          }
        }
      }

      // Handle emotion updates
      if (updates.emotion_names !== undefined) {
        // Remove existing emotions
        await supabase
          .from('decision_emotions')
          .delete()
          .eq('decision_id', id);

        // Add new emotions
        for (const emotionName of updates.emotion_names) {
          const emotion = await getOrCreateEmotion(emotionName);
          
          if (emotion) {
            const { error: linkError } = await supabase
              .from('decision_emotions')
              .insert({
                decision_id: id,
                emotion_id: emotion.id,
                user_id: user.id,
              });
            
            if (linkError) {
              console.error('Error linking emotion to decision:', linkError);
            }
          }
        }
      }

      toast.success('Decision updated successfully!');

      await openDecisionDetails(id, true);
      debouncedRefresh(true);
    } catch (error) {
      console.error('Error updating decision:', error);
      toast.error('Failed to update decision');
      throw error;
    }
  }, [user, getReviewDateLimit, debouncedRefresh, openDecisionDetails, getOrCreateTag, getOrCreateEmotion]);

  // Delete a decision
  const deleteDecision = useCallback(async (id: string) => {
    if (!user) throw new Error('User not authenticated');

    try {
      const { error } = await supabase
        .from('decisions')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;

      toast.success('Decision deleted successfully!');
      setDetailsByDecisionId((current) => {
        const next = { ...current };
        delete next[id];
        return next;
      });
      delete detailFetchedAtRef.current[id];
      debouncedRefresh(true);
    } catch (error) {
      console.error('Error deleting decision:', error);
      toast.error('Failed to delete decision');
      throw error;
    }
  }, [user, debouncedRefresh]);

  // Add a rating entry
  const addRating = useCallback(async (decisionId: string, day: number, rating: number, notes?: string, outcome?: string, lessons?: string) => {
    if (!user) throw new Error('User not authenticated');

    try {
      // Check if a rating entry already exists for this decision and day
      const { data: existingRating, error: selectError } = await supabase
        .from('rating_entries')
        .select('id')
        .eq('decision_id', decisionId)
        .eq('day', day)
        .maybeSingle();

      if (selectError) throw selectError;

      if (existingRating) {
        // Update existing rating
        const { error: updateError } = await supabase
          .from('rating_entries')
          .update({
            rating,
            notes: notes || null,
            outcome: outcome || null,
            lessons: lessons || null,
            date: new Date().toISOString(),
          })
          .eq('id', existingRating.id);

        if (updateError) throw updateError;
      } else {
        // Insert new rating
        const { error: insertError } = await supabase
          .from('rating_entries')
          .insert({
            decision_id: decisionId,
            day,
            rating,
            notes: notes || null,
            outcome: outcome || null,
            lessons: lessons || null,
            user_id: user.id,
          });

        if (insertError) throw insertError;
      }

      // Mark corresponding scheduled review as completed
      await supabase
        .from('scheduled_reviews')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString(),
        })
        .eq('decision_id', decisionId)
        .eq('day', day);

      toast.success('Rating added successfully!');

      // Refetch this decision's own rows before the summary refresh so an open
      // detail view shows the completed review straight away. An unforced
      // refresh is deferred by up to MIN_REFRESH_INTERVAL_MS (15s), which reads
      // as a save that silently failed.
      await openDecisionDetails(decisionId, true);
      debouncedRefresh(true);
    } catch (error) {
      console.error('Error adding rating:', error);
      toast.error('Failed to add rating');
      throw error;
    }
  }, [user, openDecisionDetails, debouncedRefresh]);

  // Get current rating for a decision
  const getCurrentRating = (decision: Decision): number => {
    if (!decision.rating_history || decision.rating_history.length === 0) {
      return decision.day0_rating;
    }
    
    // Return the most recent rating
    const sortedRatings = [...decision.rating_history].sort((a, b) => b.day - a.day);
    return sortedRatings[0].rating;
  };

  // Get decisions with pending reviews (due now or overdue)
  const getPendingReviews = () => {
    return decisions.filter(decision => {
      const { dueNowReviews, overdueReviews } = classifyDecisionReviews(decision);
      return dueNowReviews.length > 0 || overdueReviews.length > 0;
    });
  };

  const updateReviewStatuses = useCallback(async () => {
    debouncedRefresh();
  }, [debouncedRefresh]);

  // Initial data fetch
  useEffect(() => {
    if (user) {
      fetchDecisions();
    }
  }, [user, fetchDecisions]);

  // Update review statuses periodically (less frequently)
  useEffect(() => {
    if (!user) return;
    
    // Update immediately on mount
    updateReviewStatuses();
    
    // Then update every 5 minutes instead of constantly
    const interval = setInterval(updateReviewStatuses, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [user, updateReviewStatuses]);

  useEffect(() => {
    if (!user) return;

    const reconcile = () => debouncedRefresh(true);
    const interval = setInterval(reconcile, RECONCILIATION_INTERVAL_MS);
    window.addEventListener('focus', reconcile);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', reconcile);
    };
  }, [user, debouncedRefresh]);

  const completeReview = useCallback(async (decisionId: string, day: number, rating: number, notes?: string, outcome?: string, lessons?: string) => {
    if (!user) throw new Error('User not authenticated');

    try {
      // Add rating entry (which triggers debouncedRefresh internally)
      await addRating(decisionId, day, rating, notes, outcome, lessons);
    } catch (error) {
      console.error('Error completing review:', error);
      throw error;
    }
  }, [user, addRating]);

  return {
    decisions,
    loading,
    createDecision,
    updateDecision,
    deleteDecision,
    addRating,
    getCurrentRating,
    getPendingReviews,
    completeReview,
    loadMoreRatings,
    loadMoreReviews,
    detailsByDecisionId,
    staleDetails,
    openDecisionDetails,
    refreshDecisions: fetchDecisions,
  };
}

// `useDecisionsState` is the real implementation above. It's wrapped in a
// single shared Context/Provider pair so every component that calls
// `useDecisions()` reads from ONE fetch/ONE realtime subscription instead
// of each mounting its own independent copy (which used to multiply
// database egress by however many decision-related components were
// mounted at once).
const DecisionsContext = createContext<ReturnType<typeof useDecisionsState> | null>(null);

export function DecisionsProvider({ children }: { children: ReactNode }) {
  const value = useDecisionsState();
  return <DecisionsContext.Provider value={value}>{children}</DecisionsContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components -- the hook belongs with its provider; only fast refresh is affected.
export function useDecisions() {
  const context = useContext(DecisionsContext);
  if (!context) {
    throw new Error('useDecisions must be used within a DecisionsProvider');
  }
  return context;
}
